import {
  ApiBlueprint,
  configApiRef,
  createApiRef,
  discoveryApiRef,
  oauthRequestApiRef,
} from '@backstage/frontend-plugin-api';
import type {
  BackstageIdentityApi,
  OAuthApi,
  OpenIdConnectApi,
  ProfileInfoApi,
  SessionApi,
} from '@backstage/core-plugin-api';
import { OAuth2 } from '@backstage/core-app-api';

export const ssoRijkAuthApiRef = createApiRef<
  OAuthApi &
    OpenIdConnectApi &
    ProfileInfoApi &
    BackstageIdentityApi &
    SessionApi
>({ id: 'auth.sso-rijk' });

export const ssoRijkAuthApi = ApiBlueprint.make({
  name: 'sso-rijk-auth',
  params: defineParams =>
    defineParams({
      api: ssoRijkAuthApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        oauthRequestApi: oauthRequestApiRef,
        configApi: configApiRef,
      },
      factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
        OAuth2.create({
          configApi,
          discoveryApi,
          oauthRequestApi,
          provider: { id: 'oidc', title: 'SSO Rijk', icon: () => null },
          environment: configApi.getOptionalString('auth.environment'),
          defaultScopes: ['openid', 'profile', 'email'],
        }),
    }),
});

/**
 * The parent domain the portal and code.overheid.nl share, so a cookie set by one reaches the
 * other. On one host with several ports there is none to set: cookies ignore the port.
 */
export function sharedCookieDomain(hostname: string, codeUrl: string) {
  const own = hostname.split('.');
  const other = new URL(codeUrl).hostname.split('.');
  if (own.join('.') === other.join('.')) {
    return undefined;
  }
  const shared: string[] = [];
  while (own.length && other.length && own.at(-1) === other.at(-1)) {
    shared.unshift(own.pop()!);
    other.pop();
  }
  return shared.length >= 2 ? shared.join('.') : undefined;
}

const hintAttributes = (codeUrl: string) => {
  const domain = sharedCookieDomain(window.location.hostname, codeUrl);
  return `path=/; secure; samesite=lax${domain ? `; domain=${domain}` : ''}`;
};

/**
 * A hint between the applications: someone here has a session at the identity provider. It
 * grants nothing. It only makes trying the normal login worthwhile, and that login is what
 * checks the session.
 */
export const SESSION_HINT = 'sso-session';

export const setSessionHint = (codeUrl: string) => {
  document.cookie = `${SESSION_HINT}=1; ${hintAttributes(codeUrl)}; max-age=${8 * 60 * 60}`;
};
export const clearSessionHint = (codeUrl: string) => {
  document.cookie = `${SESSION_HINT}=; ${hintAttributes(codeUrl)}; max-age=0`;
};
export const hasSessionHint = () => document.cookie.includes(`${SESSION_HINT}=1`);

/** code.overheid.nl has a session of its own, which ends on its next page when it sees this. */
export const announceSignOut = (codeUrl: string) => {
  document.cookie = `sso-logout=1; ${hintAttributes(codeUrl)}; max-age=${8 * 60 * 60}`;
};

const LOGOUT_FLAG = 'sso-logout';

/** Signing out of the portal alone would leave the session that signs people straight back in. */
export const requestIdentityProviderLogout = () => sessionStorage.setItem(LOGOUT_FLAG, '1');

export function finishIdentityProviderLogout(baseUrl: string, issuer: string, clientId: string) {
  if (!sessionStorage.getItem(LOGOUT_FLAG)) {
    return false;
  }
  sessionStorage.removeItem(LOGOUT_FLAG);
  const portal = baseUrl.replace(/\/$/, '');
  const query = new URLSearchParams({ client_id: clientId, post_logout_redirect_uri: `${portal}/` });
  window.location.href = `${issuer.replace(/\/$/, '')}/protocol/openid-connect/logout?${query}`;
  return true;
}
